/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import {
  accessAPI,
  Boton,
  Titulo,
  Formulario,
  Sidebar,
  useFuncionalidad, tituloBase, TablaFiltrable } from "../../../../shared";
import { useNavigate } from "react-router-dom";

import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";

const SEARCH_FIELDS = ["_tipoProceso", "nombre"];

export default function CertificadoTipos() {
  const conUrsea = useFuncionalidad("URSEA");
  const [certificadoTipos, setCertificadoTipos] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [loaderEnvio, setLoaderEnvio] = useState(false);
  const navigate = useNavigate();
  const toast = useRef(null);

  useEffect(() => {
    document.title = "Tipos de certificado";
    cargarCertificadoTipos();
    return () => (document.title = tituloBase());
  }, []);

  function cargarCertificadoTipos() {
    accessAPI(
      "GET",
      "admin/producto/certificadotipo",
      null,
      (respuesta) => {
        setCertificadoTipos(respuesta);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg || "Error al cargar los tipos de certificado",
          sticky: true
        });
      }
    );
  }

  function crearCertificadoTipoSubmit(respuestasFormulario) {
    setLoaderEnvio(true);
    accessAPI(
      "POST",
      "admin/config/certificadotipo",
      respuestasFormulario,
      (respuesta) => {
        setSidebarVisible(false);
        setLoaderEnvio(false);
        navigate(`/certificadotipos/${respuesta.id}`);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al crear el tipo de certificado",
          sticky: true
        });
        setLoaderEnvio(false);
      }
    );
  }

  const crearCertificadoTipoFormConfig = {
    secciones: [
      {
        campos: [
          // El código en URSEA solo se pide si el organismo tiene la
          // integración prendida.
          ...(conUrsea
            ? [
                {
                  nombre: "codigo",
                  tipoitemformularioid: 1,
                  etiqueta: "Código URSEA"
                }
              ]
            : []),
          {
            nombre: "nombre",
            tipoitemformularioid: 1,
            etiqueta: "Nombre",
            requerido: true
          },
          {
            nombre: "reglamentacion",
            tipoitemformularioid: 1,
            etiqueta: "Reglamentación",
            requerido: true
          },
          {
            nombre: "procesotipoid",
            tipoitemformularioid: 5,
            etiqueta: "Tipo de proceso",
            opciones: "admin/config/procesotipo",
            sinOpcionInicial: true,
            requerido: true
          },
          {
            nombre: "duracioncertificado",
            tipoitemformularioid: 4,
            etiqueta: "Duración del certificado (años)",
            requerido: true
          },
          {
            nombre: "textolegalcertificado",
            tipoitemformularioid: 11,
            etiqueta: "Texto legal en el certificado",
            requerido: true
          },
          {
            nombre: "notasPropuesta",
            tipoitemformularioid: 11,
            etiqueta: "Notas para la propuesta comercial"
          },
          {
            nombre: "disponiblesolicitud",
            tipoitemformularioid: 6,
            etiqueta: "Disponible para solicitud"
          },
          {
            nombre: "disponibleproceso",
            tipoitemformularioid: 6,
            etiqueta: "Disponible para procesos"
          },
          ...(conUrsea
            ? [
                {
                  nombre: "gestionadoursea",
                  tipoitemformularioid: 6,
                  etiqueta: "Gestionado por URSEA"
                }
              ]
            : []),
          {
            nombre: "sinrenovacion",
            tipoitemformularioid: 6,
            etiqueta: "El certificado no tiene renovación"
          },
          ...(conUrsea
            ? [
                {
                  nombre: "nombreursea",
                  tipoitemformularioid: 1,
                  etiqueta: "Nombre en el certificado digital de URSEA"
                }
              ]
            : [])
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Crear tipo de certificado",
        callback: crearCertificadoTipoSubmit,
        validar: true
      }
    ]
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />

      {!certificadoTipos && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem"
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem"
            }}
          >
            <Skeleton width="15rem" height="2.5rem" />
            <Skeleton width="12rem" height="2.5rem" />
          </div>
          <Card>
            <div
              css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
            >
              <Skeleton width="100%" height="3rem" />
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} width="100%" height="3.5rem" />
              ))}
            </div>
          </Card>
        </div>
      )}

      {certificadoTipos && (
        <>
          <Titulo
            urlVolver="/config"
            botones={
              <Boton callback={() => setSidebarVisible(true)}>
                Nuevo tipo de certificado
              </Boton>
            }
          >
            Tipos de certificado
          </Titulo>
          <Card>
            <TablaFiltrable
              value={(certificadoTipos || []).map((c) => ({
                ...c,
                _tipoProceso: c.procesotipo?.nombre
              }))}
              searchFields={SEARCH_FIELDS}
              anchoBusqueda="33%"
              nombreArchivo="tipos_certificado"
              onRowClick={(e) => {
                if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
                  window.open(`/certificadotipos/${e.data.id}`, "_blank");
                } else {
                  navigate(`${e.data.id}`);
                }
              }}
              rowClassName={() => "cursor-pointer"}
              rows={10}
              stripedRows
            >
              <Column field="procesotipo.nombre" header="Tipo de Proceso" />
              <Column field="nombre" header="Tipo de Certificado" />
            </TablaFiltrable>
          </Card>
        </>
      )}

      <Sidebar
        visible={sidebarVisible}
        position="right"
        onHide={() => setSidebarVisible(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nuevo tipo de certificado</Titulo>
        <Formulario
          config={crearCertificadoTipoFormConfig}
          loader={loaderEnvio}
        />
      </Sidebar>
    </>
  );
}
